import { Calendar, DateNavigator } from "common/components";
import { DefaultButton } from "common/components/Button/new/DefaultButton";
import { Column, Row } from "common/components/Layout/new/constLayout";
import { I18N_KEYS } from "common/i18n/keys";
import { isBeforeByDate } from "common/utils/dateUtil";
import {
  closeGlobalModal,
  openGlobalModal,
} from "common/utils/modalController";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DATE_PICKER_MODE, DatePicker } from "./DatePicker";
import { ComponentSize } from "common/enum/ComponentSize";

export const ScheduleCalendar = ({
  startDate,
  endDate,
  initDate,
  extMinDate,
  extMaxDate,
  schedules,
  isRoundBorder = false,
  onSelect,
  onMonthChange,
  onClickToday,
  allowSelectOutside = false,
}) => {
  const { t } = useTranslation();
  const today = new Date(new Date().setHours(0, 0, 0, 0));

  const [currentDate, setCurrentDate] = useState(initDate || today);
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);

  useEffect(() => {
    if (initDate) setCurrentDate(initDate);
  }, [initDate]);

  useEffect(() => {
    if (extMinDate) setMinDate(new Date(extMinDate));
    if (extMaxDate) setMaxDate(new Date(extMaxDate));
  }, [extMinDate, extMaxDate]);

  const checkPreviousMonth = () => {
    if (!minDate) return false;
    const currentYM = currentDate.getFullYear() * 12 + currentDate.getMonth();
    const minYM = minDate.getFullYear() * 12 + minDate.getMonth();
    return currentYM <= minYM;
  };

  const checkNextMonth = () => {
    if (!maxDate) return false;
    const currentYM = currentDate.getFullYear() * 12 + currentDate.getMonth();
    const maxYM = maxDate.getFullYear() * 12 + maxDate.getMonth();
    return currentYM >= maxYM;
  };

  const goToPreviousMonth = () => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - 1,
      1,
    );
    setCurrentDate(newDate);
    onMonthChange?.(newDate);
  };

  const goToNextMonth = () => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      1,
    );
    setCurrentDate(newDate);
    onMonthChange?.(newDate);
  };

  const handleClickToday = () => {
    if (onClickToday) {
      onClickToday(today);
      return;
    }
    setCurrentDate(today);
  };

  return (
    <Column>
      <Row>
        <DateNavigator
          date={currentDate}
          format={"YYYY.MM"}
          onClick={() =>
            openGlobalModal({
              content: (
                <DatePicker
                  size={ComponentSize.LARGE}
                  date={currentDate}
                  minDate={minDate}
                  maxDate={maxDate}
                  onSelect={(date) => {
                    setCurrentDate(date);
                    onMonthChange?.(date);
                    closeGlobalModal();
                  }}
                  initialMode={DATE_PICKER_MODE.MONTH}
                  fixedMode
                />
              ),
            })
          }
          disablePrev={checkPreviousMonth()}
          onPrev={goToPreviousMonth}
          disableNext={checkNextMonth()}
          onNext={goToNextMonth}
        />
        <DefaultButton.White
          width={"7.5rem"}
          height={"100%"}
          onClick={handleClickToday}>
          {t(I18N_KEYS.common.today)}
        </DefaultButton.White>
      </Row>
      <Calendar
        currentDate={currentDate}
        startDate={startDate}
        endDate={endDate}
        schedules={schedules}
        isRoundBorder={isRoundBorder}
        onSelect={onSelect}
        onPrevMonth={goToPreviousMonth}
        onNextMonth={goToNextMonth}
        isDisabled={(date) =>
          !allowSelectOutside && isBeforeByDate(date, today)
        }
        allowSelectOutside={allowSelectOutside}
      />
    </Column>
  );
};
